import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../context/app.context";
import { useNavigate } from "react-router-dom";
import { faCar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import orderDetailsFactory from "./common/order-details.factory";
import orderDetailsHelper from "./common/order-details.helper";
import OrderDetailsDriversComponent from "./drivers/order-details-drivers.component";
import OrderDetailsPassengersComponent from "./passengers/order-details-passengers.component";
import OrderDetailsRouteViewMode from "./common/types/order-details-route-view-mode";
import OrderDetailsRouteComponent from "./route/order-details-route.component";
import orderDetailsWaypointFactory from "./common/order-details-waypoint.factory";
import MessengerComponent from "../../../common/components/messenger/messenger.component";
import orderDetailsPlannedRouteMapMarkersFactory from "./common/order-details-planned-route-map-markers.factory";
import OrderDetailsCargoOrder from "./common/types/order-details-cargo-order";
import orderDetailsUserPermissionsHelper from "./common/user-permissions/order-details-user-permission.helper";
import orderDetailsMapRouteFactory from "./common/order-details-map-route.factory";
import OrderDetailsPassenger from "./common/types/order-details-passenger";
import OrderDetailsDriver from "./common/types/order-details-driver";
import OrderDetailsRouteWaypoint from "./common/types/order-details-route-waypoint";
import orderDetailsCopyToClipboardHelper from "./common/copy-to-clipboard/order-details-copy-to-clipboard.helper";
import orderTranslationsHelper from "../../../languages/order-translations.helper";
import SearchRoadRoutingResponse from "../../../common/utils/search-road-route/search-road-routing.response";
import MapMarker from "../../../common/components/map/types/map-marker";
import SearchRoadRoutingRequest from "../../../common/utils/search-road-route/search-road-routing.request";
import MapRoute from "../../../common/components/map/types/map-route";
import notificationService from "../../../common/utils/notification/notification.service";
import clipboardService from "../../../common/utils/clipboard/clipboard.service";
import Row from "../../../common/components/grid/row";
import Column from "../../../common/components/grid/column";
import NoticeBoxComponent from "../../../common/components/notice-box/notice-box.component";
import NoticeBoxType from "../../../common/components/notice-box/notice-box-type";
import MapComponent from "../../../common/components/map/map.component";
import OrderDetailsHistoryComponent from "./history/order-details-history.component";
import OrderDetailsLoaderComponent from "./loader/order-details-loader.component";
import OrderDetailsErrorComponent from "./error/order-details-error.component";
import orderRoutesHelper from "../common/routes/order-routes.helper";
import OrderDetailsChangesApprovalComponent from "./changes-approval/order-details-changes-approval.component";
import OrderDetailsToolsComponent from "./tools/order-details-tools.component";
import mapMarkerIconFactory from "../../../common/components/map/marker/map-marker-icon.factory";
import useAbort from "../../../common/hooks/use-abort";
import orderService from "../../../common/services/order/order.service";
import OrderCancelOrderError from "../../../common/services/order/cancel/order/order-cancel-order-error";
import OrderCancelRequestError from "../../../common/services/order/cancel/request/order-cancel-request-error";
import useRouteQueryParams from "../../../common/hooks/use-route-query-params";
import OrderActiveListingRouteQueryParams from "../common/routes/types/order-active-listing-route-params";
import googleMapsRouteService from "../../../common/utils/google-maps-route/google-maps-route.service";
import { OrderCargoOrderActiveListItemAttentions } from "../../../common/services/order/cargo-order/active-list/order-cargo-order-active-list";
import dateService from "../../../common/utils/date/date.service";
import OrderCancelRevertRequestError from "../../../common/services/order/cancel/revert-request/order-cancel-revert-request-error";
import searchRoadRoutingService from "../../../common/utils/search-road-route/search-road-routing.service";
import useGpsCargoOrderRecords from "../../../common/services/gps/cargo-order-records/use-gps-cargo-order-records";
import GpsCargoOrderRecordsParams from "../../../common/services/gps/cargo-order-records/gps-cargo-order-records-params";
import GpsCargoOrderRecord from "../../../common/services/gps/cargo-order-records/gps-cargo-order-records";
import OrderCargoOrderNodeExclusionParams from "../../../common/services/order/cargo-order/node-exclusion/order-cargo-order-node-exclusion-params";
import useOrderCargoOrderNodeExclusion from "../../../common/services/order/cargo-order/node-exclusion/use-order-cargo-order-node-exclusion";
import useOrderCargoOrderDetailsComplex from "../../../common/services/order/cargo-order/details-complex/use-order-cargo-order-details-complex";
import OrderCargoOrderDetailsComplexParams from "../../../common/services/order/cargo-order/details-complex/order-cargo-order-details-complex-params";
import OrderCargoOrderDetailsComplex from "../../../common/services/order/cargo-order/details-complex/order-cargo-order-details-complex";
import { debounce } from "lodash";
import OrderDetailsToolsProps, {
  OrderDetailsToolsPropsStatus,
} from "./tools/types/order-details-tools";

type OrderDetailsProps = {
  orderUuid: string;
  isOrderFinished?: boolean;
  cargoCompanyOrderId: number;
  /**TODO detach active listing specific type CargoOrderActiveListItemAttentions from here */
  attentions?: OrderCargoOrderActiveListItemAttentions;
  isVisible: boolean;
  cargoCompanyUuid?: string;
  newTabPath?: string;
  hasAccessToOptimizer?: boolean;
  onCancelOrderSuccess?: () => void;
  onAddCandidatureSuccess?: () => void;
  executionStatus?: OrderDetailsToolsPropsStatus;
  refreshListing?: () => void;
};

const OrderDetailsComponent: FC<OrderDetailsProps> = (props) => {
  const { user } = useAppContext();
  const navigate = useNavigate();
  const [routeQueryParams, setRouteQueryParams] =
    useRouteQueryParams<OrderActiveListingRouteQueryParams>();

  const leftColumnContentRef = useRef<HTMLDivElement>(null);
  const rightColumnContentHeight = useMemo(() => {
    return leftColumnContentRef.current?.offsetHeight || 500;
  }, [leftColumnContentRef.current?.offsetHeight]);

  const userPermissions = orderDetailsUserPermissionsHelper.getPermissions(
    user?.roles!,
  );

  const translations = orderTranslationsHelper.getDetailsTranslations();

  const [passengers, setPassengers] = useState<OrderDetailsPassenger[]>([]);
  const [drivers, setDrivers] = useState<OrderDetailsDriver[]>([]);
  const [waypoints, setWaypoints] = useState<OrderDetailsRouteWaypoint[]>([]);
  const [plannedMapRoutes, setPlannedMapRoutes] = useState<MapRoute[]>([]);

  const gpsCargoOrderRecords = useGpsCargoOrderRecords(false);
  const gpsCargoOrderRecordsAbort = useAbort();

  const cargoOrderNodeExclusion = useOrderCargoOrderNodeExclusion();
  const cargoOrderNodeExclusionAbort = useAbort();

  const cargoOrderDetailsComplex = useOrderCargoOrderDetailsComplex();
  const cargoOrderDetailsComplexAbort = useAbort();

  const [completedMapRoute, setCompletedMapRoute] = useState<MapRoute | null>(
    null,
  );
  const [shouldShowCompletedMapRoutes, setShouldShowCompletedMapRoutes] =
    useState(false);

  const [routeViewMode, setRouteViewMode] = useState<OrderDetailsRouteViewMode>(
    OrderDetailsRouteViewMode.ORDERED,
  );
  const [isOrderSolved, setIsOrderSolved] = useState(false);

  const [selectedPassenger, setSelectedPassenger] =
    useState<OrderDetailsPassenger | null>(null);

  const [cargoOrderDetails, setCargoOrderDetails] =
    useState<OrderDetailsCargoOrder | null>(null);

  const [hoveredMarker, setHoveredMarker] = useState<MapMarker | null>(null);

  const [orderHistoryRefetchFlag, setOrderHistoryRefetchFlag] = useState(false);
  const [isCancelOrderFetching, setIsCancelOrderFetching] = useState(false);
  const [isCancelOrderRequestFetching, setIsCancelOrderRequestFetching] =
    useState(false);

  const [isRouteStarted, setIsRouteStarted] = useState(false);
  const [shouldShowOrderHistory, setShouldShowOrderHistory] = useState(false);

  const [planEntryUuid, setPlanEntryUuid] = useState<string>();

  const orderCancelAbort = useAbort();
  const orderCancelRequestAbort = useAbort();
  const revertOrderCancelRequestAbort = useAbort();

  const isApproveButtonDisabled = props.attentions
    ? !(
        props.attentions.cargoOrderCancel ||
        props.attentions.cargoOrderEdit ||
        props.attentions.newPlanEntryPost ||
        props.attentions.newSharedPost
      )
    : true;

  const setWaypointsForOrder = (
    responseCargoOrder: OrderCargoOrderDetailsComplex,
  ) => {
    const waypoints = orderDetailsWaypointFactory.createWaypoints(
      responseCargoOrder.transportingOrders,
    );

    setWaypoints(waypoints);
  };

  useEffect(() => {
    if (!cargoOrderDetailsComplex.data) {
      return;
    }

    return setWaypointsForOrder(cargoOrderDetailsComplex.data);
  }, [isOrderSolved, cargoOrderDetailsComplex.data, routeViewMode]);

  const fetchRidesNodeExclusions = () => {
    const params: OrderCargoOrderNodeExclusionParams = {
      orderUuid: props.orderUuid,
    };

    cargoOrderNodeExclusion.load(params, cargoOrderNodeExclusionAbort.signal);
  };

  useEffect(() => {
    if (!cargoOrderDetailsComplex.data) return;

    setPlanEntryUuid(
      cargoOrderDetailsComplex.data.transportingOrders[0].planEntryUuid ??
        undefined,
    );

    const passengers = orderDetailsFactory.createPassengers(
      cargoOrderDetailsComplex.data.transportingOrders,
    );

    const responseCandidateDrivers = orderDetailsHelper.getCandidateDrivers(
      cargoOrderDetailsComplex.data.transportingOrders,
    );

    const drivers = orderDetailsFactory.createDrivers(
      cargoOrderDetailsComplex.data.transportingOrders,
      responseCandidateDrivers,
    );

    const isOrderSolved =
      !!cargoOrderDetailsComplex.data.transportingOrders.length;

    setPassengers(passengers);
    setDrivers(drivers);

    setIsOrderSolved(isOrderSolved);
    setRouteViewMode(
      isOrderSolved
        ? OrderDetailsRouteViewMode.SOLVED
        : OrderDetailsRouteViewMode.ORDERED,
    );

    const isRouteStarted =
      cargoOrderDetailsComplex.data.transportingOrders.some(
        (transportingOrder) => transportingOrder.solve,
      );

    setIsRouteStarted(isRouteStarted);

    const cargoOrderDetails = orderDetailsFactory.createBasicInfo(
      cargoOrderDetailsComplex.data,
    );

    setCargoOrderDetails(cargoOrderDetails);
    fetchRidesNodeExclusions();
  }, [cargoOrderDetailsComplex.data]);

  const fetchDetails = () => {
    const params: OrderCargoOrderDetailsComplexParams = {
      orderUuid: props.orderUuid,
    };

    cargoOrderDetailsComplex.load(params, cargoOrderDetailsComplexAbort.signal);
  };

  const onCargoExternalIdChangeSuccess = () => {
    fetchDetails();
  };

  useEffect(() => {
    if (!props.isVisible || waypoints.length) {
      return;
    }

    fetchDetails();

    return cargoOrderDetailsComplexAbort.revoke;
  }, [props.isVisible]);

  const driverPositionMapMarker: MapMarker | undefined = useMemo(() => {
    if (shouldShowCompletedMapRoutes && completedMapRoute?.waypoints?.length) {
      const lastWaypoint =
        completedMapRoute?.waypoints[completedMapRoute.waypoints.length - 1];

      if (!lastWaypoint) {
        return undefined;
      }

      return {
        coordinate: {
          latitude: lastWaypoint?.latitude,
          longitude: lastWaypoint?.longitude,
        },
        icon: mapMarkerIconFactory.createStandardIcon({
          content: (
            <div>
              <FontAwesomeIcon icon={faCar} />
            </div>
          ),
        }),
        isDriverMarker: true,
      };
    }
    return undefined;
  }, [shouldShowCompletedMapRoutes, completedMapRoute]);

  const locationsMapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers = orderDetailsPlannedRouteMapMarkersFactory
      .createMapMarkers(waypoints, selectedPassenger?.uuid)
      .map((marker) => ({ ...marker, isRouteMarker: true }));

    return mapMarkers;
  }, [selectedPassenger?.uuid, waypoints]);

  const onMarkerHover = (marker: MapMarker | null) => {
    setHoveredMarker(marker);
  };

  const generateTimeBasedMarkers = (
    completedRoute: GpsCargoOrderRecord[],
    existingRouteMarkers: MapMarker[],
  ): MapMarker[] => {
    if (!completedRoute || completedRoute.length === 0) {
      return [];
    }

    const markers: MapMarker[] = [];
    const parseTime = (timestamp: Date): number =>
      new Date(timestamp).getTime();

    let startTime = parseTime(completedRoute[0].recordTime);
    let nextMarkerTime = startTime;

    let index = 0;

    completedRoute.forEach((currPoint) => {
      let currTime = parseTime(currPoint.recordTime);

      if (currTime >= nextMarkerTime) {
        nextMarkerTime = currTime + 60000;
        const date = new Date(currPoint.recordTime);

        if (dateService.checkIsValidDate(date)) {
          const formattedTime = dateService.formatTime(date);

          const newMarker = {
            coordinate: { latitude: currPoint.lat, longitude: currPoint.lon },
            icon: mapMarkerIconFactory.createGpsIcon(),
            title: formattedTime,
            isGpsRouteMarker: true,
            onMouseOver: () =>
              onMarkerHover({
                coordinate: {
                  latitude: currPoint.lat,
                  longitude: currPoint.lon,
                },
                icon: mapMarkerIconFactory.createGpsIcon(),
                title: formattedTime,
              }),
            onMouseOut: () => onMarkerHover(null),
            index: index,
          };

          index++;

          const isDuplicate = existingRouteMarkers.some(
            (routeMarker) =>
              routeMarker.coordinate.latitude ===
                newMarker.coordinate.latitude &&
              routeMarker.coordinate.longitude ===
                newMarker.coordinate.longitude &&
              routeMarker.isGpsRouteMarker,
          );

          if (!isDuplicate) {
            markers.push(newMarker);
          }
        }
      }
    });

    return markers;
  };

  const mapMarkers: MapMarker[] = useMemo(() => {
    if (driverPositionMapMarker) {
      return [...locationsMapMarkers, driverPositionMapMarker];
    }

    return [...locationsMapMarkers];
  }, [locationsMapMarkers, driverPositionMapMarker]);

  const mapRoutes: MapRoute[] = useMemo(() => {
    const routes: MapRoute[] = [...plannedMapRoutes];

    if (shouldShowCompletedMapRoutes && completedMapRoute) {
      routes.push(completedMapRoute);
    }

    return routes;
  }, [plannedMapRoutes, completedMapRoute, shouldShowCompletedMapRoutes]);

  const memoizedMarkers = useMemo(() => {
    if (!completedMapRoute) {
      return (
        <MapComponent
          key={
            hoveredMarker?.coordinate.latitude +
            "-" +
            hoveredMarker?.coordinate.longitude
          }
          markers={mapMarkers}
          autoFit
          routes={mapRoutes}
        />
      );
    }

    const gpsMarkers: MapMarker[] = generateTimeBasedMarkers(
      gpsCargoOrderRecords.data,
      locationsMapMarkers,
    );

    return (
      <MapComponent
        key={
          hoveredMarker?.coordinate.latitude +
          "-" +
          hoveredMarker?.coordinate.longitude
        }
        markers={mapMarkers}
        gpsMarkers={gpsMarkers}
        autoFit
        routes={mapRoutes}
        shouldShowCompletedMapRoutes={shouldShowCompletedMapRoutes}
      />
    );
  }, [
    shouldShowCompletedMapRoutes,
    gpsCargoOrderRecords.data,
    mapMarkers,
    mapRoutes,
  ]);

  const onPassengerRowClick = (passenger: OrderDetailsPassenger) => {
    if (passenger.uuid === selectedPassenger?.uuid) {
      setSelectedPassenger(null);
      return;
    }
    setSelectedPassenger(passenger);
  };

  const isRouteSequenceSolved =
    orderDetailsHelper.checkIsRouteSequenceSolved(waypoints);

  const isRouteResolved = isRouteSequenceSolved || !!drivers.length;

  const onTryAgainButtonClick = () => {
    fetchDetails();
  };

  const handleRouteResponse = (response: SearchRoadRoutingResponse) => {
    const mapRoutes = orderDetailsMapRouteFactory.createPlannedMapRoutes(
      response.routes,
    );
    setPlannedMapRoutes(mapRoutes);
  };

  const fetchRouteForSolvedOrder = () => {
    const request: SearchRoadRoutingRequest = {
      waypointCoordinates: waypoints.map((waypoint) => ({
        latitude: waypoint.place.latitude,
        longitude: waypoint.place.longitude,
      })),
      excludeHighway: cargoOrderNodeExclusion.data?.excludeInside,
    };

    searchRoadRoutingService.search(request).then(handleRouteResponse);
  };

  const fetchRoute = () => {
    if (isRouteResolved) {
      fetchRouteForSolvedOrder();
      return;
    }

    const waypointsCouldBeConnected =
      orderDetailsHelper.getWaypointGroupsCouldBeConnected(waypoints);

    const coordinatesOfWaypointsCouldBeConnected =
      orderDetailsHelper.getCoordinatesOfWaypointsCouldBeConnected(
        waypointsCouldBeConnected,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    coordinatesOfWaypointsCouldBeConnected.forEach((coordinates) => {
      const request: SearchRoadRoutingRequest = {
        waypointCoordinates: coordinates,
        excludeHighway: cargoOrderNodeExclusion.data?.excludeInside,
      };

      const fetch = searchRoadRoutingService.search(request);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];

      responses.forEach((response) => {
        const mapRoute = response.routes[0]
          ? orderDetailsMapRouteFactory.createPlannedMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }
      });

      const newMapRoutes: MapRoute[] = mapRouteWaypointGroups.map(
        (routeWaypointGroup) => ({
          waypoints: routeWaypointGroup,
        }),
      );

      setPlannedMapRoutes(newMapRoutes);
    });
  };

  useEffect(() => {
    if (
      !waypoints.length ||
      !props.isVisible ||
      plannedMapRoutes.length ||
      cargoOrderNodeExclusion.data?.excludeInside === undefined
    ) {
      return;
    }

    fetchRoute();
  }, [waypoints, props.isVisible, cargoOrderNodeExclusion.data]);

  useEffect(() => {
    const completedMapRoute: MapRoute =
      orderDetailsMapRouteFactory.createCompletedMapRoute(
        gpsCargoOrderRecords.data,
      );

    setCompletedMapRoute(completedMapRoute);
  }, [gpsCargoOrderRecords.data]);

  useEffect(() => {
    if (!gpsCargoOrderRecords.isError) return;

    notificationService.error(
      translations.failureFetchingCompletedRouteNotificationMessageText,
    );

    setShouldShowCompletedMapRoutes(false);
  }, [gpsCargoOrderRecords.isError]);

  const fetchCompletedRoute = () => {
    const params: GpsCargoOrderRecordsParams = {
      orderUuid: props.orderUuid,
    };

    gpsCargoOrderRecords.load(params, gpsCargoOrderRecordsAbort.signal);
  };

  useEffect(() => {
    if (!shouldShowCompletedMapRoutes) {
      setCompletedMapRoute(null);

      return;
    }

    fetchCompletedRoute();
  }, [shouldShowCompletedMapRoutes]);

  const toggleCompletedMapRoutesActive = () => {
    setShouldShowCompletedMapRoutes((curr) => !curr);
  };

  const toggleGoogleMapsRoute = () => {
    const googleMapsRouteUrl =
      isRouteSequenceSolved && !!waypoints.length
        ? googleMapsRouteService.createGoogleMapsRouteUrl(
            waypoints.map((waypoint) => ({
              latitude: waypoint.place.latitude,
              longitude: waypoint.place.longitude,
            })),
          )
        : "";

    window.open(googleMapsRouteUrl, "_blank", "noopener,noreferrer");
  };

  const onCopyDetailsButtonClick = () => {
    const textToCopy = orderDetailsCopyToClipboardHelper.getTextToCopy(
      user!,
      cargoOrderDetailsComplex.data?.humanId,
      waypoints,
      passengers,
      cargoOrderDetails?.routeNumber!,
      cargoOrderDetails?.contactPassengerAfterMinutes!,
    );

    clipboardService.copy(textToCopy);
    notificationService.info(translations.copyOrderDetailsNotificationText);
  };

  const onCancelOrderFetchSuccess = () => {
    const successMessage =
      orderDetailsHelper.getOrderCancellationSuccessMessage(
        cargoOrderDetails?.cargoCompanyOrderId!,
      );

    notificationService.success(successMessage);

    props.onCancelOrderSuccess && props.onCancelOrderSuccess();
  };

  const onCancelOrderFetchFailure = (error: OrderCancelOrderError) => {
    notificationService.error(error.message);
  };

  const cancelOrder = async () => {
    if (!cargoOrderDetails) return;

    setIsCancelOrderFetching(true);

    try {
      await orderService.cancel(
        {
          orderUuid: cargoOrderDetails.cargoOrderId,
        },
        orderCancelAbort.signal,
      );

      onCancelOrderFetchSuccess();
    } catch (error) {
      onCancelOrderFetchFailure(error as OrderCancelOrderError);
    } finally {
      setIsCancelOrderFetching(false);
    }
  };

  const onOrderCancellationRequestSentSuccess = () => {
    const successMessage =
      orderDetailsHelper.getOrderCancellationRequestSuccessMessage(
        cargoOrderDetails?.cargoCompanyOrderId!,
      );

    notificationService.success(successMessage);

    setCargoOrderDetails((currBasicInfo) => ({
      ...currBasicInfo!,
      isCancelRequestSent: true,
    }));
  };

  const onRevertOrderCancellationRequestSentSuccess = () => {
    const successMessage =
      orderDetailsHelper.getRevertOrderCancellationRequestSuccessMessage(
        cargoOrderDetails?.cargoCompanyOrderId!,
      );

    notificationService.success(successMessage);

    setCargoOrderDetails((currBasicInfo) => ({
      ...currBasicInfo!,
      isCancelRequestSent: false,
    }));
  };

  const onOrderCancellationRequestSentFailure = (
    error: OrderCancelRequestError,
  ) => {
    notificationService.error(error.message);
  };

  const onRevertOrderCancellationRequestSentFailure = (
    error: OrderCancelRevertRequestError,
  ) => {
    notificationService.error(error.message);
  };

  const sendOrderCancellationRequest = async () => {
    if (!cargoOrderDetails) return;

    setIsCancelOrderRequestFetching(true);

    try {
      await orderService.sendCancelRequest(
        {
          orderUuid: cargoOrderDetails.cargoOrderId,
        },
        orderCancelRequestAbort.signal,
      );

      onOrderCancellationRequestSentSuccess();
    } catch (error) {
      onOrderCancellationRequestSentFailure(error as OrderCancelRequestError);
    } finally {
      setIsCancelOrderRequestFetching(false);
    }
  };

  const sendRevertOrderCancellationRequest = async () => {
    if (!cargoOrderDetails) return;

    setIsCancelOrderRequestFetching(true);

    try {
      await orderService.sendRevertCancelRequest(
        {
          orderUuid: cargoOrderDetails.cargoOrderId,
        },
        revertOrderCancelRequestAbort.signal,
      );

      onRevertOrderCancellationRequestSentSuccess();
    } catch (error) {
      onRevertOrderCancellationRequestSentFailure(
        error as OrderCancelRevertRequestError,
      );
    } finally {
      setIsCancelOrderRequestFetching(false);
    }
  };

  const onEditOrderButtonClick = () => {
    const newSearchParams = { ...routeQueryParams };
    newSearchParams.defaultSelectedOrderUuid = props.orderUuid;
    setRouteQueryParams(newSearchParams);
    navigate(orderRoutesHelper.getEditRoute({ orderId: props.orderUuid }));
  };

  const onOrderHistoryButtonClick = () => {
    if (shouldShowOrderHistory) {
      setShouldShowOrderHistory(false);
      return;
    }

    setShouldShowOrderHistory(true);
  };

  const onAddCandidatureSuccess = useCallback(
    debounce(() => {
      fetchDetails();
      props.onAddCandidatureSuccess && props.onAddCandidatureSuccess();
    }, 500),
    [],
  );

  const updateTaxiCorporationUuid = (taxiCorporationUuid: string) => {
    setCargoOrderDetails((curr) => ({
      ...curr!,
      taxiCorporationUuid,
    }));
  };

  const onEditTaxiCorporationSuccess = (newTaxiCorporationUuid: string) => {
    updateTaxiCorporationUuid(newTaxiCorporationUuid);
  };

  const isCancelRequestNoticeBoxVisible =
    !!cargoOrderDetails?.isCancelRequestSent &&
    !cargoOrderDetails.isFinished &&
    !cargoOrderDetails.isCancelled;

  const isCompletedRouteButtonActive =
    shouldShowCompletedMapRoutes &&
    !!completedMapRoute &&
    !gpsCargoOrderRecords.isLoading;

  const refetchOrderHistory = () => {
    setOrderHistoryRefetchFlag((curr) => !curr);
  };

  const onSuccessApproveChangesByDealer = () => {
    refetchOrderHistory();
    setCargoOrderDetails((curr) => ({
      ...curr!,
      changesApprovals: {
        ...curr!.changesApprovals,
        isDealerApproveNeeded: false,
      },
    }));
  };

  const onSuccessApproveChangesByDispatcher = () => {
    refetchOrderHistory();
    setCargoOrderDetails((curr) => ({
      ...curr!,
      changesApprovals: {
        ...curr!.changesApprovals,
        isDispatcherApproveNeeded: false,
      },
    }));
  };

  const onSuccessApproveChangesByOperator = () => {
    refetchOrderHistory();
    setCargoOrderDetails((curr) => ({
      ...curr!,
      changesApprovals: {
        ...curr!.changesApprovals,
        isOperatorApproveNeeded: false,
      },
    }));
  };

  const onFilteredOrderButtonClick = () => {
    window.open(props.newTabPath, "_blank", "noopener,noreferrer");
  };

  const onOrderAccept = () => {
    props.refreshListing && props.refreshListing();
  };

  const onOrderReject = () => {
    props.refreshListing && props.refreshListing();
  };

  const orderDetailsToolsProps: OrderDetailsToolsProps = {
    drivers,
    userPermissions,
    isRouteSequenceSolved,
    cargoOrderDetails,
    cargoCompanyUuid: props.cargoCompanyUuid ?? "",
    isRouteStarted,
    isCancelOrderFetching,
    isCompletedRoutesFetching: gpsCargoOrderRecords.isLoading,
    isCompletedRouteButtonActive,
    isCancelOrderRequestFetching,
    isOptimizerVisible: props.hasAccessToOptimizer ?? false,
    shouldShowOrderHistory,
    executionStatus: props.executionStatus ?? null,
    onOrderAccept,
    onOrderReject,
    cancelOrder,
    onEditTaxiCorporationSuccess,
    onAddCandidatureSuccess,
    onCopyDetailsButtonClick,
    onEditOrderButtonClick,
    onOrderHistoryButtonClick,
    toggleCompletedMapRoutesActive,
    toggleGoogleMapsRoute,
    onCargoExternalIdChangeSuccess,
    onFilteredOrderButtonClick,
    sendOrderCancellationRequest,
    sendRevertOrderCancellationRequest,
  };

  const messengerChannelsAvailability = useMemo(
    () =>
      orderDetailsHelper.getMessengerChannelAvailability(
        cargoOrderDetails?.solvedOrderId,
      ),
    [cargoOrderDetails?.solvedOrderId],
  );

  const DetailsContent = (
    <Row>
      <Column lg={7}>
        <div ref={leftColumnContentRef}>
          <Row>
            <Column>
              {isCancelRequestNoticeBoxVisible && (
                <>
                  <NoticeBoxComponent
                    type={NoticeBoxType.DANGER}
                    classNames={{ root: "mb-2" }}
                  >
                    {translations.cancelOrderRequestNoticeText}
                  </NoticeBoxComponent>
                </>
              )}
              <div className="order_details_map_wrapper">
                {memoizedMarkers}
                <OrderDetailsToolsComponent {...orderDetailsToolsProps} />
              </div>
            </Column>
          </Row>
          <Row>
            <Column>
              {!isRouteResolved && (
                <small className="order_details_not_completed_route_notification">
                  {translations.notResolvedRouteNotification}
                </small>
              )}
            </Column>
            <Column>
              <div
                className="order_details_cargo_external_id_wrapper"
                title={`${translations.cargoExternalOrderIdLabel}: ${
                  cargoOrderDetails?.cargoCompanyExternalOrderId ?? "-----"
                }`}
              >
                {`${translations.cargoExternalOrderIdLabel}: ${
                  cargoOrderDetails?.cargoCompanyExternalOrderId ?? "-----"
                }`}
              </div>
            </Column>
            <Column md={4} lg={3}>
              <OrderDetailsPassengersComponent
                onPassengerRowClick={onPassengerRowClick}
                passengers={passengers}
                selectedPassenger={selectedPassenger}
                waypoints={waypoints}
              />
              <OrderDetailsDriversComponent
                drivers={drivers}
                planEntryUuid={
                  props.isOrderFinished ? planEntryUuid : undefined
                }
                orderUuid={props.orderUuid}
              />
            </Column>
            <Column md={8} lg={9}>
              <OrderDetailsRouteComponent
                isRouteSequenceSolved={isRouteSequenceSolved}
                waypoints={waypoints}
                orderUuid={props.orderUuid}
                selectedPassenger={selectedPassenger}
              />
            </Column>
          </Row>
        </div>
      </Column>
      <Column lg={5}>
        <div style={{ height: rightColumnContentHeight }}>
          {shouldShowOrderHistory ? (
            <div className="order_details_history_column">
              <OrderDetailsChangesApprovalComponent
                orderUuid={props.orderUuid}
                isOrderCancelled={
                  cargoOrderDetailsComplex.data?.isCancelled ?? false
                }
                changeApprovals={cargoOrderDetails?.changesApprovals!}
                onSuccessApproveByDealer={onSuccessApproveChangesByDealer}
                onSuccessApproveByDispatcher={
                  onSuccessApproveChangesByDispatcher
                }
                onSuccessApproveByOperator={onSuccessApproveChangesByOperator}
                isApproveButtonDisabled={isApproveButtonDisabled}
              />
              <div className="order_details_history_column__history_wrapper">
                <OrderDetailsHistoryComponent
                  orderUuid={props.orderUuid}
                  refetchFlag={orderHistoryRefetchFlag}
                />
              </div>
            </div>
          ) : (
            <MessengerComponent
              key={cargoOrderDetails?.solvedOrderId}
              channelsAvailability={messengerChannelsAvailability}
              orderUuid={props.orderUuid}
              solvedOrderUuid={cargoOrderDetails?.solvedOrderId}
            />
          )}
        </div>
      </Column>
    </Row>
  );

  const ErrorContent = (
    <OrderDetailsErrorComponent
      cargoCompanyOrderId={props.cargoCompanyOrderId}
      isFetching={cargoOrderDetailsComplex.isLoading}
      onTryAgainButtonClick={onTryAgainButtonClick}
    />
  );

  const LoaderContent = <OrderDetailsLoaderComponent />;

  return (
    <div className="order_details">
      {cargoOrderDetailsComplex.isError
        ? ErrorContent
        : cargoOrderDetailsComplex.isLoading
          ? LoaderContent
          : DetailsContent}
    </div>
  );
};

export default OrderDetailsComponent;
